/*! Copyright (c) Safe As Milk. All rights reserved. */
import { debounce } from "utils";

class StickyScroll extends HTMLElement {
    #ticking;
    #topGap;
    #maxDisplacement;
    #scrollPosition;
    #intersectionObserver;
    #topPosition;
    #isProductPage;
    #galleryColumn;
    #primaryBlocks;
    #productContent;
    #initialOffsetTop;
    #elementWidth;
    #isFixed;
    
    constructor() {
        super();
        this.#topGap = 18;
        this.#intersectionObserver = null;
        this.#isProductPage = false;
        this.#galleryColumn = null;
        this.#primaryBlocks = null;
        this.#productContent = null;
        this.#initialOffsetTop = 0;
        this.#elementWidth = 0;
        this.#isFixed = false;
    }
    
    connectedCallback() {
        // Check if we're on a product page
        this.#productContent = this.closest('.product-single__content');
        this.#isProductPage = !!this.#productContent;
        
        if (this.#isProductPage && window.innerWidth >= 980) {
            this.#galleryColumn = this.#productContent.querySelector('.product-single__media');
            this.#primaryBlocks = this.closest('.product-single__primary-blocks');
            this.#setupProductPageSticky();
        } else if (!this.#isProductPage) {
            this.#setupDefaultSticky();
        }
    }
    
    #setupProductPageSticky() {
        if (!this.#galleryColumn || !this.#primaryBlocks) return;
        
        this.#setTopGap();
        
        // Store initial position and width
        const rect = this.#primaryBlocks.getBoundingClientRect();
        this.#initialOffsetTop = window.scrollY + rect.top;
        this.#elementWidth = rect.width;
        
        const handleScroll = () => {
            if (!this.#ticking) {
                window.requestAnimationFrame(() => {
                    this.#updateProductPageSticky();
                    this.#ticking = false;
                });
                this.#ticking = true;
            }
        };
        
        const recalculate = () => {
            this.#setTopGap();
            // Temporarily reset to get true position
            this.style.position = '';
            this.style.top = '';
            this.style.width = '';
            this.style.right = '';
            this.#isFixed = false;
            
            requestAnimationFrame(() => {
                const rect = this.#primaryBlocks.getBoundingClientRect();
                this.#initialOffsetTop = window.scrollY + rect.top;
                this.#elementWidth = rect.width;
                
                if (window.innerWidth >= 980) {
                    this.#updateProductPageSticky();
                }
            });
        };
        
        window.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", debounce(recalculate, 100));
        
        // Initial update
        this.#updateProductPageSticky();
    }
    
    #updateProductPageSticky() {
        if (!this.#galleryColumn || !this.#primaryBlocks) return;
        
        const scrollY = window.scrollY;
        const galleryRect = this.#galleryColumn.getBoundingClientRect();
        const primaryBlocksRect = this.#primaryBlocks.getBoundingClientRect();
        
        // Calculate when we should start being fixed
        const shouldBeFixed = scrollY >= this.#initialOffsetTop - this.#topGap;
        
        // Calculate when gallery ends
        // Gallery bottom relative to document
        const galleryBottomAbsolute = scrollY + galleryRect.bottom;
        
        // Element height (use scrollHeight to include overflow)
        const elementHeight = this.scrollHeight;
        
        // Where the bottom of fixed element would be
        const fixedBottomAbsolute = scrollY + this.#topGap + elementHeight;
        
        // Should stop being fixed when element bottom would go past gallery bottom
        const shouldStop = fixedBottomAbsolute > galleryBottomAbsolute;
        
        if (shouldBeFixed && !shouldStop) {
            // Be fixed
            this.style.position = 'fixed';
            this.style.top = `${this.#topGap}px`;
            this.style.width = `${this.#elementWidth}px`;
            this.style.maxHeight = '';
            this.style.overflowY = '';
            this.#isFixed = true;
        } else if (shouldStop) {
            // Stop at gallery end - position absolutely within primary-blocks
            // Calculate the top position to place element at gallery end
            const stopTop = galleryBottomAbsolute - elementHeight - this.#initialOffsetTop;
            
            this.style.position = 'relative';
            this.style.top = `${Math.max(0, stopTop)}px`;
            this.style.width = '';
            this.style.maxHeight = '';
            this.style.overflowY = '';
            this.#isFixed = false;
        } else {
            // At top, not scrolled yet
            this.style.position = '';
            this.style.top = '';
            this.style.width = '';
            this.style.maxHeight = '';
            this.style.overflowY = '';
            this.#isFixed = false;
        }
    }
    
    #setupDefaultSticky() {
        // Original sticky behavior for non-product pages
        const scrollListener = () => {
            if (!this.#ticking) {
                window.requestAnimationFrame(() => {
                    this.#setStickyDisplacement();
                    this.#ticking = false;
                });
                this.#ticking = true;
            }
        };
        
        const handleIntersection = entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    window.addEventListener("scroll", scrollListener);
                } else {
                    window.removeEventListener("scroll", scrollListener);
                }
            });
        };
        
        const updateMaxDisplacement = () => {
            const getHeightWithBottomMargin = () => {
                const paddingBottom = Number(window.getComputedStyle(this).getPropertyValue("padding-bottom").replace("px", ""));
                if (!paddingBottom) {
                    this.style.paddingBottom = "1px";
                    const fullHeight = this.offsetHeight - 1;
                    this.style.paddingBottom = null;
                    return fullHeight;
                } else {
                    return this.offsetHeight;
                }
            };
            this.#maxDisplacement = window.innerHeight - getHeightWithBottomMargin() - 18;
        };
        
        const updateObserver = () => {
            this.#setTopGap();
            if (!this.#topPosition) this.#topPosition = this.#topGap;
            updateMaxDisplacement();
            this.#scrollPosition = window.scrollY;
            this.#setStyles();
            
            if (window.innerHeight < this.offsetHeight) {
                if (!this.#intersectionObserver) {
                    this.#intersectionObserver = new IntersectionObserver(handleIntersection.bind(this), {
                        rootMargin: "0px"
                    });
                }
                this.#intersectionObserver.observe(this);
                this.classList.add("is-scrollable");
                this.classList.remove("is-not-scrollable");
            } else if (window.innerHeight >= this.offsetHeight && this.#intersectionObserver) {
                this.#intersectionObserver.disconnect();
                window.removeEventListener("scroll", scrollListener);
                this.classList.add("is-not-scrollable");
                this.classList.remove("is-scrollable");
            }
        };
        
        updateObserver();
        window.addEventListener("resize", debounce(updateObserver, 100));
        const resizeObserver = new ResizeObserver(debounce(updateObserver, 100));
        resizeObserver.observe(this);
    }
    
    #setTopGap() {
        const header = document.querySelector("header");
        this.#topGap = header && header.getAttribute("data-sticky-header") === "true" ? header.offsetHeight + 18 : 18;
    }
    
    #setStyles() {
        this.style.display = "block";
        this.style.position = "sticky";
        this.style.top = `${this.#topPosition}px`;
        this.classList.add("is-not-scrollable");
    }
    
    #setStickyDisplacement() {
        const stickyElementTop = Number(this.style.top.replace("px", ""));
        if (window.scrollY < this.#scrollPosition) {
            if (stickyElementTop < this.#topGap) {
                const topMath = stickyElementTop + this.#scrollPosition - window.scrollY;
                this.style.top = `${topMath}px`;
                this.#topPosition = topMath;
                if (topMath >= this.#topGap) {
                    this.style.top = `${this.#topGap}px`;
                    this.#topPosition = this.#topGap;
                }
            } else if (stickyElementTop >= this.#topGap) {
                this.style.top = `${this.#topGap}px`;
                this.#topPosition = this.#topGap;
            }
        } else if (stickyElementTop > this.#maxDisplacement) {
            const topMath = stickyElementTop + this.#scrollPosition - window.scrollY;
            this.style.top = `${topMath}px`;
            this.#topPosition = topMath;
            if (topMath <= this.#maxDisplacement) {
                this.style.top = `${this.#maxDisplacement}px`;
                this.#topPosition = this.#maxDisplacement;
            }
        } else if (stickyElementTop <= this.#maxDisplacement) {
            this.style.top = `${this.#maxDisplacement}px`;
            this.#topPosition = this.#maxDisplacement;
        }
        this.#scrollPosition = window.scrollY;
    }
}

customElements.define("sticky-scroll", StickyScroll);
//# sourceMappingURL=sticky-scroll.js.map
